"use client";
"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.TextField = void 0;
const react_1 = __importStar(require("react"));
const foundations_1 = require("../../foundations");
const DropdownMenu_1 = require("./DropdownMenu");
const TextField = ({ label, value, error, type = "text", name, description, disabled, icon, readOnly, required, autoComplete = "off", spellCheck = "false", dropdownOptions = [], noResultsMessage, searchIcon, optionDisplayCount, onClickClear, onSelectOption, onEnter, onBlur, onChange, onIconClick }) => {
    const inputId = (0, react_1.useId)();
    const [isFocused, setIsFocused] = react_1.default.useState(false);
    const clearButtonRef = react_1.default.useRef(null);
    const { inputRef, focusedOptionIdx, handleKeyDown, showDropdown, iconButtonRef, handleClickOption, ariaPropsForDropdown, handleCompositionStart, handleCompositionEnd } = (0, DropdownMenu_1.useDropdownMenu)({
        isSelect: type === "select",
        dropdownOptions,
        onSelectOption,
        isFocused,
        onEnter,
        onClickClear
    });
    const shouldShowLabel = isFocused || !!value;
    const showNoResults = noResultsMessage !== undefined &&
        value !== undefined &&
        typeof value === "string" &&
        value.length > 0 &&
        (dropdownOptions === null || dropdownOptions === void 0 ? void 0 : dropdownOptions.length) === 0;
    const handleFocus = () => {
        setIsFocused(true);
    };
    const handleBlur = react_1.default.useCallback((event) => {
        if (clearButtonRef.current &&
            event.relatedTarget === clearButtonRef.current &&
            onClickClear) {
            onClickClear();
        }
        handleClickOption(event);
        setIsFocused(false);
        if (onBlur) {
            onBlur(event);
        }
    }, [onBlur, handleClickOption, onClickClear]);
    const handleIconClick = (e) => {
        e.preventDefault();
        if (inputRef.current) {
            const inputLength = inputRef.current.value.length;
            if (onIconClick) {
                onIconClick();
            }
            // use setTimeout because focus and cursor position should update after DOM updates
            setTimeout(() => {
                if (inputLength) {
                    inputRef.current.focus();
                    inputRef.current.setSelectionRange(inputLength, inputLength);
                }
            }, 0);
        }
    };
    const labelTextStyles = react_1.default.useMemo(() => {
        const labelYPosition = shouldShowLabel ? "-24px" : "-50%";
        const labelScale = shouldShowLabel ? 0.66 : 1;
        return {
            position: "absolute",
            left: "spacing-4",
            top: "50%",
            transform: `translateY(${labelYPosition}) scale(${labelScale})`,
            transformOrigin: "left center",
            transition: "transition-ease-out-fast",
            pointerEvents: "none",
            fontSize: "font-size-1",
            lineHeight: "leading-2",
            letterSpacing: "kerning-2",
            color: disabled ? "text-incidental" : "text-secondary",
            whiteSpace: "nowrap",
            overflow: "hidden"
        };
    }, [disabled, shouldShowLabel]);
    const inputStyles = react_1.default.useMemo(() => {
        return Object.assign(Object.assign({ width: "100%", height: "spacing-10", px: "spacing-4", paddingTop: shouldShowLabel ? "spacing-7" : "spacing-5", paddingBottom: shouldShowLabel ? "spacing-3" : "spacing-5", borderRadius: "radius-2", cursor: disabled || readOnly ? "not-allowed" : "auto" }, (disabled && { pointerEvents: "none" })), { overflow: "hidden" });
    }, [shouldShowLabel, disabled, readOnly]);
    const descriptionTextStyles = react_1.default.useMemo(() => {
        return {
            color: disabled ? "text-incidental" : "text-secondary"
        };
    }, [disabled]);
    const inputBorderStyles = react_1.default.useMemo(() => {
        return Object.assign(Object.assign({ position: "relative", border: "1px solid", borderColor: error
                ? "input-stroke-error"
                : isFocused
                    ? "focus"
                    : "input-stroke-enabled", borderRadius: "radius-2", marginBottom: error ? "spacing-2" : "spacing-1", cursor: disabled || readOnly ? "not-allowed" : "auto", backgroundColor: disabled || readOnly ? "feedback-surface-disabled" : "input-onSurface" }, (readOnly && {
            borderWidth: "0 0 1px 0",
            borderRadius: "unset"
        })), { overflow: "hidden" });
    }, [disabled, error, isFocused, readOnly]);
    return (react_1.default.createElement(foundations_1.Box, { sx: {
            display: "flex",
            flexDirection: "column",
            userSelect: "none",
            position: "relative"
        } },
        react_1.default.createElement(foundations_1.Box, Object.assign({ sx: Object.assign({}, inputBorderStyles) }, ariaPropsForDropdown),
            react_1.default.createElement(foundations_1.Box, { as: "label", htmlFor: inputId, id: `${inputId}-label`, sx: Object.assign({}, labelTextStyles) },
                react_1.default.createElement(foundations_1.Text, { variant: "body-1-regular" }, label),
                required && (react_1.default.createElement(foundations_1.Text, { variant: "body-1-regular", sx: {
                        color: "feedback-onSurface-incidental-critical"
                    } }, "*"))),
            react_1.default.createElement(foundations_1.Box, { as: "input", ref: inputRef, type: type, id: inputId, value: value, name: name, disabled: disabled, readOnly: readOnly, autoComplete: autoComplete, spellCheck: spellCheck, "aria-disabled": disabled, "aria-invalid": !!error, "aria-readonly": readOnly, "aria-required": required, onChange: onChange, onBlur: handleBlur, onFocus: handleFocus, onKeyDown: handleKeyDown, onCompositionStart: handleCompositionStart, onCompositionEnd: handleCompositionEnd, sx: Object.assign(Object.assign({}, inputStyles), { justifyContent: "space-between" }) }),
            react_1.default.createElement(foundations_1.Box, { sx: {
                    position: "absolute",
                    top: "50%",
                    right: searchIcon ? "spacing-2" : "spacing-4",
                    transform: "translateY(-50%)",
                    display: "flex",
                    alignItems: "center",
                    gap: "spacing-3",
                    pointerEvents: "auto"
                } },
                onClickClear && value && (react_1.default.createElement(foundations_1.Target, { ref: clearButtonRef, "aria-label": "clear", tabIndex: -1, sx: {
                        display: "flex",
                        alignItems: "center"
                    }, onClick: onClickClear },
                    react_1.default.createElement(foundations_1.Icon, { icon: "x-circle", size: "xs", color: "control-enabled" }))),
                icon && (react_1.default.createElement(foundations_1.Target, { "aria-hidden": onIconClick ? "false" : "true", "aria-label": onIconClick ? "click icon" : undefined, onClick: handleIconClick, sx: {
                        display: "flex",
                        alignItems: "center"
                    } },
                    react_1.default.createElement(foundations_1.Icon, { icon: icon, size: "xs", color: "control-enabled" }))),
                searchIcon && (react_1.default.createElement(foundations_1.Box, { ref: iconButtonRef, sx: { display: "flex", alignItems: "center" } }, searchIcon())))),
        showDropdown && (react_1.default.createElement(DropdownMenu_1.DropdownMenu, { dropdownOptions: dropdownOptions, showNoResults: showNoResults, focusedIdx: focusedOptionIdx, optionDisplayCount: optionDisplayCount, noResultsMessage: noResultsMessage })),
        description && (react_1.default.createElement(foundations_1.Text, { variant: "body-2-regular", sx: Object.assign({}, descriptionTextStyles) }, description)),
        error && (react_1.default.createElement(foundations_1.Text, { variant: "body-2-regular", sx: { color: "input-stroke-error" } }, error))));
};
exports.TextField = TextField;
//# sourceMappingURL=TextField.js.map